import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Gavel, Pencil, Plus, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1";

function ItemEditor({ title, prefix, items, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { label: "", amount: 0 }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls}>{title}</label>
        <button type="button" onClick={add} className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add line
        </button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-gray-400">No line items.</p>}
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={it.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Description"
              className={`${inputCls} flex-1`} data-testid={`${prefix}-label-${i}`} />
            <input type="number" step="0.01" value={it.amount} onChange={(e) => update(i, "amount", e.target.value)} placeholder="0.00"
              className={`${inputCls} w-32`} data-testid={`${prefix}-amount-${i}`} />
            <button type="button" onClick={() => remove(i)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ClientCaseDetails({ clientId }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ accident_types: [] });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/case-details`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clients/case-details/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const startEdit = () => {
    setForm({
      accident_date: data.accident_date || "", accident_type: data.accident_type || "",
      accident_location: data.accident_location || "", accident_description: data.accident_description || "",
      police_report_no: data.police_report_no || "", liability_notes: data.liability_notes || "",
      economic_items: data.economic_items || [], noneconomic_items: data.noneconomic_items || [],
      include_medical_billed: data.include_medical_billed !== false,
    });
    setEditing(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        economic_items: form.economic_items.map((i) => ({ label: i.label, amount: Number(i.amount) || 0 })),
        noneconomic_items: form.noneconomic_items.map((i) => ({ label: i.label, amount: Number(i.amount) || 0 })),
      };
      const { data: updated } = await api.put(`/clients/${clientId}/case-details`, payload);
      setData(updated);
      setEditing(false);
      toast.success("Case details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save case details");
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const t = data.totals || {};
  const tr = data.treatment || {};

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-case-details-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <Gavel className="w-4 h-4 text-gray-400" /> Case Details
        </h3>
        {!editing && (
          <button onClick={startEdit} data-testid="edit-case-details-button"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-5" data-testid="case-details-view">
          <div className="grid grid-cols-2 gap-4">
            <div><p className={labelCls}>Accident type</p><p className="text-sm text-gray-800">{data.accident_type || "—"}</p></div>
            <div><p className={labelCls}>Accident date</p><p className="text-sm text-gray-800">{data.accident_date || "—"}</p></div>
            <div><p className={labelCls}>Location</p><p className="text-sm text-gray-800">{data.accident_location || "—"}</p></div>
            <div><p className={labelCls}>Police report #</p><p className="text-sm text-gray-800">{data.police_report_no || "—"}</p></div>
          </div>
          {data.accident_description && (
            <div><p className={labelCls}>Description</p><p className="text-sm text-gray-700 whitespace-pre-wrap">{data.accident_description}</p></div>
          )}
          {data.liability_notes && (
            <div><p className={labelCls}>Liability notes</p><p className="text-sm text-gray-700 whitespace-pre-wrap">{data.liability_notes}</p></div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <p className={labelCls}>Treatment roll-up</p>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="treatment-rollup">
              <div className="bg-[#002FA7]/5 rounded-sm p-3">
                <p className="font-heading text-lg font-extrabold text-[#002FA7]">{fmt(tr.total_billed)}</p>
                <p className="text-xs font-semibold text-gray-500">Medical billed</p>
              </div>
              <div className="bg-rose-50 rounded-sm p-3">
                <p className="font-heading text-lg font-extrabold text-rose-700">{fmt(tr.total_liens)}</p>
                <p className="text-xs font-semibold text-gray-500">Liens</p>
              </div>
              <div className="bg-gray-50 rounded-sm p-3">
                <p className="font-heading text-lg font-extrabold text-gray-800">{tr.providers || 0}</p>
                <p className="text-xs font-semibold text-gray-500">Providers</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className={labelCls}>Damages &amp; demand</p>
            <div className="mt-1 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Economic</span><span className="font-semibold text-gray-900">{fmt(t.economic_total)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Non-economic</span><span className="font-semibold text-gray-900">{fmt(t.noneconomic_total)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Medical in demand</span><span className="font-semibold text-gray-900">{fmt(t.medical_in_demand)}</span></div>
              <div className="flex justify-between border-t border-gray-200 mt-2 pt-2">
                <span className="font-bold text-gray-900">Total demand</span>
                <span className="font-heading font-extrabold text-[#002FA7] text-lg" data-testid="total-demand">{fmt(t.total_demand)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="case-details-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Accident type</label>
              <select value={form.accident_type} onChange={(e) => set("accident_type", e.target.value)} className={inputCls} data-testid="accident-type-select">
                <option value="">Select…</option>
                {(meta.accident_types || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Accident date</label>
              <input type="date" value={form.accident_date} onChange={(e) => set("accident_date", e.target.value)} className={inputCls} data-testid="accident-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Location</label>
              <input value={form.accident_location} onChange={(e) => set("accident_location", e.target.value)} className={inputCls} data-testid="accident-location-input" />
            </div>
            <div>
              <label className={labelCls}>Police report #</label>
              <input value={form.police_report_no} onChange={(e) => set("police_report_no", e.target.value)} className={inputCls} data-testid="police-report-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.accident_description} onChange={(e) => set("accident_description", e.target.value)} rows={3} className={inputCls} data-testid="accident-description-input" />
          </div>
          <div>
            <label className={labelCls}>Liability notes</label>
            <textarea value={form.liability_notes} onChange={(e) => set("liability_notes", e.target.value)} rows={2} className={inputCls} data-testid="liability-notes-input" />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <ItemEditor title="Economic damages" prefix="economic-damage" items={form.economic_items} onChange={(v) => set("economic_items", v)} />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <ItemEditor title="Non-economic damages" prefix="noneconomic-damage" items={form.noneconomic_items} onChange={(v) => set("noneconomic_items", v)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.include_medical_billed} onChange={(e) => set("include_medical_billed", e.target.checked)} className="accent-[#002FA7]" data-testid="include-medical-check" />
            Include total medical billed in demand
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEditing(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-1.5">
              <X className="w-4 h-4" /> Cancel
            </button>
            <button onClick={save} disabled={saving} data-testid="save-case-details-button"
              className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
